import React, { useEffect, useRef, useState } from 'react';
import {
    EAutoRange,
    EDragMode,
    FastLineRenderableSeries,
    NumericAxis,
    SciChartSurface,
    XAxisDragModifier,
    XyDataSeries,
    YAxisDragModifier,
    ZoomPanModifier,
    SciChartJsNavyTheme,
    EAxisAlignment,
    ENumericFormat
} from "scichart";
import axios from 'axios';
import { backendlink } from '../../config';
import "./doctor-patient-ecg-datagrid.scss"
import { LoadIndicator } from 'devextreme-react';

const SciChartExample = ({ ECGDataID, patientToken, user }) => {
    const chartContainerRef = useRef(null); // Reference for the chart container
    const sciChartSurfaceRef = useRef(null);

    const [ecgDataInfo, setecgDataInfo] = useState([]);
    const [loading, setLoading] = useState(false); // State to track loading status


    SciChartSurface.useWasmFromCDN();

    useEffect(() => {
        const drawChart = async () => {
            setLoading(true); // Set loading to true before fetching data
            try {
                const response = await axios.post(backendlink + 'doctor/getpatientecglinedata', {
                    patienttoken: patientToken,
                    ecgdataid: ECGDataID
                }, {
                    headers: {
                        token: user.token,
                    },
                });

                setecgDataInfo(response.data.result[0]);

                const ecgData = response.data.result[0].arrECGdata
                    .flat()
                    .map((value) => (value == null ? NaN : value)); // Replace null with NaN


                ecgData[0] = ecgData[0] || 0; // Ensure the first value is not NaN

                // Create a SciChartSurface with the Navy theme
                const { wasmContext, sciChartSurface } = await SciChartSurface.create(chartContainerRef.current, {
                    theme: new SciChartJsNavyTheme()
                });

                sciChartSurfaceRef.current = sciChartSurface;
                console.log("SciChartSurface created");

                sciChartSurface.background = "white";

                // Create an XAxis with fixed range and tick spacing
                const xAxis = new NumericAxis(wasmContext, {
                    autoRange: EAutoRange.Never,
                    visibleRange: { min: 0, max: 3000 },
                    axisTitle: "Time (ms)",
                    axisTitleStyle: {
                        fontFamily: 'Open Sans',
                        fontWeight: '400',
                        fontSize: 12,
                        lineHeight: '16px',
                        letterSpacing: '0px',
                        textAlign: 'center',
                        color: '#181C23', // Optional: Add color if needed
                    },
                    labelStyle: {
                        fontFamily: 'Open Sans',
                        fontWeight: '400',
                        fontSize: 12,
                        lineHeight: '16px',
                        letterSpacing: '0px',
                        textAlign: 'center',
                        color: '#181C23', // Optional: Add color if needed
                    },
                    majorGridLineStyle: { strokeThickness: 2, color: "#FFDAD6" },
                    minorGridLineStyle: { strokeThickness: 1, color: "#FFDAD6" },
                    majorTickLineStyle: { strokeThickness: 2, color: "#FFDAD6" },
                    minorTickLineStyle: { strokeThickness: 1, color: "#FFDAD6" },
                    autoTicks: false,
                    majorDelta: 200,
                    minorDelta: 40,
                    labelPrecision: 0,
                    labelFormat: ENumericFormat.Decimal
                });
                sciChartSurface.xAxes.add(xAxis);

                const firstValue = ecgData[0];
                const visibleRangeMin = firstValue - 2.5;
                const visibleRangeMax = firstValue + 2.5;

                // Create a YAxis
                const yAxis = new NumericAxis(wasmContext, {
                    autoRange: EAutoRange.Never,
                    visibleRange: { min: visibleRangeMin, max: visibleRangeMax },
                    axisTitle: "Amplitude (mV)",
                    axisTitleStyle: {
                        fontSize: 12,
                        color: "#181C23"
                    },
                    labelStyle: {
                        color: "#181C23"
                    },
                    majorGridLineStyle: { strokeThickness: 2, color: "#FFDAD6" },
                    minorGridLineStyle: { strokeThickness: 1, color: "#FFDAD6" },
                    majorTickLineStyle: { strokeThickness: 2, color: "#FFDAD6" },
                    minorTickLineStyle: { strokeThickness: 1, color: "#FFDAD6" },
                    axisAlignment: EAxisAlignment.Left
                });
                sciChartSurface.yAxes.add(yAxis);

                // Add interactivity modifiers
                sciChartSurface.chartModifiers.add(
                    new ZoomPanModifier(),
                    new XAxisDragModifier({ dragMode: EDragMode.Panning }),
                    new YAxisDragModifier({ dragMode: EDragMode.Panning })
                );

                // Create a DataSeries
                const dataSeries = new XyDataSeries(wasmContext, { containsNaN: true, isSorted: true });

                // Populate the DataSeries with ECG data
                const xValues = ecgData.map((_, index) => index * 2); // Convert to milliseconds
                const yValues = ecgData;
                dataSeries.appendRange(xValues, yValues);

                // Create a FastLineRenderableSeries bound to the dataSeries and add to the chart
                sciChartSurface.renderableSeries.add(
                    new FastLineRenderableSeries(wasmContext, {
                        dataSeries,
                        strokeThickness: 2,
                        stroke: "black"
                    })
                );

                sciChartSurface.invalidateElement();

                return { wasmContext, sciChartSurface };
            } catch (error) {
                console.error("Error creating SciChartSurface:", error);
            }
            finally {
                setLoading(false); // Set loading to false after fetching data
            }
        };

        if (ECGDataID) {
            drawChart();
        }

        // Cleanup on component unmount
        return () => {
            if (sciChartSurfaceRef.current) {
                sciChartSurfaceRef.current.delete();
                sciChartSurfaceRef.current = null;
            }
        };
    }, [ECGDataID]);

    const formatUnixTime = (unixTime) => {
        const date = new Date(unixTime * 1000); // Convert Unix time to milliseconds
        const hours = String(date.getHours()).padStart(2, '0'); // Get local hours
        const minutes = String(date.getMinutes()).padStart(2, '0'); // Get local minutes
        const seconds = String(date.getSeconds()).padStart(2, '0'); // Get local seconds
        return `${hours}:${minutes}:${seconds}`; // Format as hh:mm:ss
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', marginLeft: '10px', height: '500px' }}>
            <div className="titles" style={{ textAlign: 'left', width: '100%' }}>
                Selected ECG Graph
            </div>
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '300px', // Adjust height to center vertically
                        width: '100%', // Full width
                    }}
                >
                    <LoadIndicator
                        width={50}
                        height={50}
                    />
                </div>) : (
                    <>
                    
                <table className='custom-table' style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', textAlign: 'left' }}>
                <thead>
                    <tr>
                        <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>Time:</th>
                        <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {formatUnixTime(ecgDataInfo.start)} - {formatUnixTime(ecgDataInfo.end)}
                        </td>
                        <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>ECG Mode:</th>
                        <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>{ecgDataInfo.ecgMode}</td>
                    </tr>
                    <tr>
                        <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>Average Heart Rate:</th>
                        <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>{Math.floor(ecgDataInfo.averageBpm)}</td>

                        <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>AF Probrability:</th>
                        <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>{ecgDataInfo.afProb}</td>
                    </tr>
                </thead>
            </table>
            </>)}
            <div ref={chartContainerRef} style={{ width: '100%', height: '500px' }}></div>
        </div>
    );
};

export default SciChartExample;